import { defineStore } from 'pinia'
import { getUser } from '../utils/helpers';
import axios from 'axios';
import VueCookies from 'vue-cookies'
import { useDemoStore } from './demoStore'
import { useAnalyzeStore } from './analyzeStore'
import { clearPendingTeamLink } from '../utils/pendingTeamLink'

let pendingUserFetch = null
let pendingUserFetchToken = null

function clearAuthCookie() {
  document.cookie = 'teamwrkr-token=; Max-Age=0; path=/'
}

// spec-ask-ux-impersonation-and-composer: the super user's own token is kept for the tab only
// (sessionStorage, never localStorage or a cookie) so Exit still works after a reload. The
// banner snapshot (target name + company name, nothing secret) rides along so the restored banner
// can still name the company; the impersonation itself is re-detected from /me, never from here.
const ORIGINAL_TOKEN_KEY = 'teamwrkr-impersonation-original-token'
const IMPERSONATION_SNAPSHOT_KEY = 'teamwrkr-impersonation-snapshot'

function sessionGet(key) {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function sessionSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    // storage unavailable (private mode, blocked) — Exit falls back to sign-in again
  }
}

function clearImpersonationSession() {
  try {
    window.sessionStorage.removeItem(ORIGINAL_TOKEN_KEY)
    window.sessionStorage.removeItem(IMPERSONATION_SNAPSHOT_KEY)
  } catch {
    // ignore
  }
}

// True when the token's (unverified) JWT payload carries the impersonation claim that
// createImpersonationToken / createUserToken put on impersonation tokens. Client-side only, used
// to decide whether a stored original token may be restored — the server still verifies everything.
export function isImpersonationToken(token) {
  try {
    const raw = String(token || '').replace(/^JWT\s+/i, '').trim()
    const part = raw.split('.')[1]
    if (!part) return false
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=')
    const payload = JSON.parse(window.atob(base64))
    return payload?.impersonation === true
  } catch {
    return false
  }
}

function readImpersonationSnapshot() {
  try {
    const parsed = JSON.parse(sessionGet(IMPERSONATION_SNAPSHOT_KEY) || 'null')
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null,
    notifications: [],
    lastRefreshed: 0,
    loading: false,
    hydrated: false,
    isImpersonating: false,
    impersonatedUser: null,
    originalToken: null,
    impersonatorIsSuperUser: false,
  }),
  actions: {
    async fetchUser(token, options = {}) {
      const { force = false } = options

      if (!token) {
        this.clearUser()
        return null
      }

      if (!force && this.user?._id && this.hydrated) {
        return this.user
      }

      if (!force && pendingUserFetch && pendingUserFetchToken === token) {
        return pendingUserFetch
      }

      this.loading = true
      pendingUserFetchToken = token

      pendingUserFetch = (async () => {
        try {
          const userData = await getUser(token);

          if (!userData || !userData._id) {
            throw new Error('Invalid user data received from user endpoint')
          }

          this.user = userData;
          this.syncImpersonation(userData)
          if (userData.token) {
            VueCookies.set('teamwrkr-token', userData.token)
          }
          this.setNotifications(userData.notifications || []);
          this.lastRefreshed = Date.now();
          this.hydrated = true

          await this.getNotifications({
            headers: { Authorization: token },
          })

          return this.user
        } catch (err) {
          console.error('Failed to fetch user:', err)
          if (err?.response?.status === 401) {
            clearAuthCookie()
            clearImpersonationSession()
          }
          this.user = null
          this.notifications = []
          this.lastRefreshed = 0
          this.hydrated = true
          throw err
        } finally {
          this.loading = false
          pendingUserFetch = null
          pendingUserFetchToken = null
        }
      })()

      return pendingUserFetch
    },
    async getNotifications(config) {
      try {
        const notifications = await axios.get(
          `${process.env.VUE_APP_API_URL}/users/notifications`,
          config
        )
        this.setNotifications(notifications.data);
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
      }
    },
    setUser(data) {
      this.user = data
      if (data?.token) {
        VueCookies.set('teamwrkr-token', data.token)
      }
      this.hydrated = true
    },
    setNotifications(data) {
      this.notifications = data
    },
    clearUser() {
      clearImpersonationSession()
      this.user = null
      this.notifications = []
      this.lastRefreshed = 0
      this.hydrated = true
    },

    // /me says whether the current session is an impersonation (buildAuthJSON's
    // `impersonation.active`). That flag, not anything in the browser, decides the banner: a page
    // reload restores it, and a real session (no flag) clears any leftover impersonation state.
    syncImpersonation(userData) {
      if (userData?.impersonation?.active === true) {
        const snapshot = readImpersonationSnapshot()
        const sameTarget = snapshot && String(snapshot._id || '') === String(userData._id || '')
        this.isImpersonating = true
        // The in-memory company name only carries over when it describes this same user.
        const current = this.impersonatedUser
        const currentSame = current && String(current._id || '') === String(userData._id || '')
        this.impersonatedUser = {
          ...(currentSame ? current : {}),
          _id: userData._id,
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          company: userData.company ?? null,
          companyName: (currentSame && current.companyName) || (sameTarget ? snapshot.companyName || '' : ''),
        }
        if (!this.originalToken) this.originalToken = sessionGet(ORIGINAL_TOKEN_KEY)
        if (!this.impersonatorIsSuperUser && sameTarget) {
          this.impersonatorIsSuperUser = snapshot.impersonatorIsSuperUser === true
        }
        return
      }
      // Not an impersonated session: drop any leftover stored original token, whatever the
      // in-memory state says, so it can never be revived later.
      clearImpersonationSession()
      if (this.isImpersonating) {
        this.isImpersonating = false
        this.impersonatedUser = null
        this.originalToken = null
        this.impersonatorIsSuperUser = false
      }
    },

    startImpersonation(token, targetUser) {
      const currentToken = VueCookies.get('teamwrkr-token')
      if (this.isImpersonating || isImpersonationToken(currentToken)) {
        // Already impersonating: the current cookie is not the super user's own token, so never
        // persist it as the "original" — keep the one already stored (if any).
        this.originalToken = this.originalToken || sessionGet(ORIGINAL_TOKEN_KEY) || null
      } else {
        this.originalToken = currentToken
        // Captured before this.user is overwritten with the impersonated target's
        // profile, so the real superuser's identity survives for the rest of the session.
        this.impersonatorIsSuperUser = this.user?.isSuperUser === true
      }
      this.isImpersonating = true
      this.impersonatedUser = targetUser
      if (this.originalToken) sessionSet(ORIGINAL_TOKEN_KEY, this.originalToken)
      sessionSet(IMPERSONATION_SNAPSHOT_KEY, JSON.stringify({
        _id: targetUser?._id ?? null,
        companyName: targetUser?.companyName || '',
        impersonatorIsSuperUser: this.impersonatorIsSuperUser,
      }))
      VueCookies.set('teamwrkr-token', token)
    },

    // Returns true when the super user's own session was restored. False means there was nothing
    // to return to — no stored original token (e.g. lost with the tab), or the current cookie is no
    // longer an impersonation session (cleared or replaced) — and the caller signs the user out.
    async stopImpersonation() {
      const original = this.originalToken || sessionGet(ORIGINAL_TOKEN_KEY)
      const stillImpersonating = isImpersonationToken(VueCookies.get('teamwrkr-token'))
      this.isImpersonating = false
      this.impersonatedUser = null
      this.originalToken = null
      this.impersonatorIsSuperUser = false
      clearImpersonationSession()
      if (original && stillImpersonating) {
        VueCookies.set('teamwrkr-token', original)
        await this.fetchUser(original, { force: true })
        return true
      }
      return false
    },

    logout() {
      const demoStore = useDemoStore()
      demoStore.resetDemoSession()
      useAnalyzeStore().clearOnboarding()
      clearPendingTeamLink()
      clearImpersonationSession()
      this.clearUser()
      clearAuthCookie()
      this.$reset()
    },
  }
})
